import { useState, useEffect, useCallback } from 'react';
import {
  platformAnalyticsAPI,
  type PlatformOverview,
  type CompanyEngagement,
  type UserActivityItem,
  type ProcessAdoptionItem,
} from '../../../lib/api';
import type { PlatformTrendPoint, FeatureUsageItem } from '../../../lib/api/platformAnalytics';

export interface DateRange {
  date_from: string;
  date_to: string;
}

export interface PlatformAnalyticsData {
  overview: PlatformOverview | null;
  companies: CompanyEngagement[];
  users: UserActivityItem[];
  adoption: ProcessAdoptionItem[];
  trend: PlatformTrendPoint[];
  features: FeatureUsageItem[];
}

/**
 * Loads all portal engagement datasets for the analytics dashboard.
 * @param dateRange - Optional ISO date bounds
 * @param companyTypeFilter - supplier | customer | empty for all
 */
export function usePlatformAnalytics(
  dateRange: DateRange,
  companyTypeFilter: string,
) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlatformAnalyticsData>({
    overview: null,
    companies: [],
    users: [],
    adoption: [],
    trend: [],
    features: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      ...(dateRange.date_from && { date_from: dateRange.date_from }),
      ...(dateRange.date_to && { date_to: dateRange.date_to }),
      ...(companyTypeFilter && { company_type: companyTypeFilter }),
    };
    try {
      const [ov, ce, ua, pa, trend, features] = await Promise.all([
        platformAnalyticsAPI.getOverview(params),
        platformAnalyticsAPI.getCompanyEngagement({ ...params, limit: 25 }),
        platformAnalyticsAPI.getUserActivity({ ...params, limit: 40 }),
        platformAnalyticsAPI.getProcessAdoption(params),
        platformAnalyticsAPI.getActivityTrend(params),
        platformAnalyticsAPI.getFeatureUsage(params),
      ]);
      setData({
        overview: ov.data as unknown as PlatformOverview,
        companies: (ce.data as unknown as { companies: CompanyEngagement[] }).companies || [],
        users: (ua.data as unknown as { users: UserActivityItem[] }).users || [],
        adoption: (pa.data as unknown as { adoption: ProcessAdoptionItem[] }).adoption || [],
        trend: (trend.data as unknown as { trend: PlatformTrendPoint[] }).trend || [],
        features: (features.data as unknown as { features: FeatureUsageItem[] }).features || [],
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load engagement data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [dateRange, companyTypeFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { loading, error, data, refetch: fetchData };
}

/** Default to the last 30 days. */
export function defaultDateRange(): DateRange {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return {
    date_from: from.toISOString().split('T')[0],
    date_to: to.toISOString().split('T')[0],
  };
}
